import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBatteryFull, faBatteryHalf, faBatteryQuarter, faSun, faDroplet, faLeaf, faTemperatureHalf, faClock, faWater, faMicrochip, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate } from '../utils/date';
import { useNavigate } from 'react-router-dom';
import Tooltip from './Tooltip';
import LocateButton from './LocateButton';
import { useAlertAcknowledgements } from '../hooks/useAlertAcknowledgements';

const SENSORS = [
        { type: 'temp', icon: faTemperatureHalf, iconClass: 'text-gray-400' },
        { type: 'light', icon: faSun, iconClass: 'text-status-warn' },
        { type: 'moist', icon: faDroplet, iconClass: 'text-blue-500' },
        { type: 'nutrition', icon: faLeaf, iconClass: 'text-status-ok' },
];

const DeviceCard = ({ device, onMoveDevice, onDeviceUpdate, onRenameDevice, onLocate, dragProps }) => {
        const navigate = useNavigate();
        const [showHint, setShowHint] = useState(false);
        const { isAcknowledged, acknowledge } = useAlertAcknowledgements(device.id);

        const getAlertForSensor = (sensorType) => {
                if (!device.alerts || device.alerts.length === 0) return null;
                return device.alerts.find(a => a.sensor_type === sensorType) || null;
        };

        const getBatteryIcon = (level) => {
                if (level > 19) return { icon: faBatteryFull, className: '' };
                if (level > 9) return { icon: faBatteryHalf, className: '' };
                return { icon: faBatteryQuarter, className: '' };
        };

        const getSensorStatus = (sensorType) => {
                if (device.created_at && (Date.now() - new Date(device.created_at).getTime()) < 24 * 60 * 60 * 1000) {
                        return {
                                label: '--',
                                message: "Getting to know your plant. The plants health will be visible as soon as we have collected enough data.",
                                className: 'bg-gray-100 text-gray-500'
                        };
                }
                const alert = getAlertForSensor(sensorType);
                if (alert) {
                        return {
                                label: alert.action_title,
                                message: alert.action_message,
                                className: isAcknowledged(alert) ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800',
                                alert
                        };
                }
                return {
                        label: 'Good',
                        message: null,
                        className: 'bg-green-50 text-green-800'
                };
        };

        const getWaterStatus = () => {
                if (device.water_level < 10) {
                        return { className: 'text-red-500' };
                }
                return { className: 'text-blue-500' };
        };

        const battery = device.battery_level !== undefined ? device.battery_level : '--';
        const batteryInfo = battery !== '--' ? getBatteryIcon(battery) : null;
        const dateTime = device.updated_at ? formatRelativeDate(device.updated_at) : 'Never';

        var imgSrc;
        if (device.image === null) {
                imgSrc = 'https://storage.googleapis.com/uxpilot-auth.appspot.com/e4c5def903-e02e5cf35c228c7398d8.png'
        } else {
                imgSrc = `/api/v2/devices/${device.id}/image`;
        }


        return (
                <div
                        id={`plant-card-${device.id}`}
                        data-testid="device-card"
                        data-device-id={device.id}
                        data-device-name={device.name}
                        className="relative bg-white rounded-2xl p-6 sm:p-8 card-shadow cursor-pointer hover:shadow-2xl transition-all duration-200"
                        onClick={() => navigate(`/device/${device.id}`)}
                        onMouseEnter={() => setShowHint(true)}
                        onMouseLeave={() => setShowHint(false)}
                        {...dragProps}
                >
                        <div className="absolute top-6 right-6 sm:top-8 sm:right-8 flex items-center bg-brand/10 rounded-full h-8 px-2.5 transition-all duration-200 overflow-hidden">
                                <span className={`text-sm font-medium text-brand whitespace-nowrap transition-all duration-200 ${showHint ? 'max-w-[120px] opacity-100 mr-2' : 'max-w-0 opacity-0 mr-0'}`}>View Details</span>
                                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-brand text-sm flex-shrink-0" />
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-6">
                                <div className="flex items-center space-x-3">
                                        <LocateButton device={device} onLocate={onLocate} />
                                        <h3 data-testid="device-card-name" className="text-lg font-semibold text-text-strong">{device.name}</h3>
                                        {device.water_level_sensor && (
                                                <div className="flex items-center gap-1.5">
                                                        <FontAwesomeIcon icon={faWater} className={getWaterStatus().className} />
                                                        <span className={`text-sm font-semibold ${getWaterStatus().className}`}>{device.water_level}%</span>
                                                </div>
                                        )}
                                </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-6">
                                <div className="flex-shrink-0">
                                        <img className="w-54 h-54 rounded-2xl object-cover" src={imgSrc} alt="plant" />
                                </div>
                                <div className="flex-shrink-0">
                                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
                                                {SENSORS.map(({ type, icon, iconClass }) => {
                                                        const status = getSensorStatus(type);
                                                        const tileClass = `rounded-[14px] pt-[1.8rem] pb-[1.8rem] text-center ${status.className}`;
                                                        const content = (
                                                                <>
                                                                        <FontAwesomeIcon icon={icon} className={`${iconClass} text-2xl mb-2`} />
                                                                        <div className="text-l font-bold text-text-strong mb-1">{status.label}</div>
                                                                </>
                                                        );
                                                        // An open alert is acknowledged by clicking its tile; once
                                                        // acknowledged it stays yellow and can't be undone.
                                                        const ackable = status.alert && !isAcknowledged(status.alert);
                                                        return (
                                                                <Tooltip key={type} content={status.message} disabled={!status.message}>
                                                                        {ackable ? (
                                                                                <button
                                                                                        type="button"
                                                                                        onClick={(e) => { e.stopPropagation(); acknowledge(status.alert); }}
                                                                                        title="Click to acknowledge"
                                                                                        data-testid={`${type}-status`}
                                                                                        data-acknowledged="false"
                                                                                        className={`w-full ${tileClass} hover:ring-2 hover:ring-yellow-300 transition-shadow`}
                                                                                >
                                                                                        {content}
                                                                                </button>
                                                                        ) : (
                                                                                <div data-testid={`${type}-status`} data-acknowledged={status.alert ? 'true' : undefined} className={tileClass}>
                                                                                        {content}
                                                                                </div>
                                                                        )}
                                                                </Tooltip>
                                                        );
                                                })}
                                        </div>
                                </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-y-2 text-sm text-text-weak border-t pt-4">
                                <div className="flex items-center space-x-2">
                                        <FontAwesomeIcon icon={faClock} />
                                        <span>Last update: {dateTime}</span>
                                </div>
                                <div class="flex items-center space-x-4">
                                </div>

                                <div className="text-right">
                                        <div className="flex items-center justify-end space-x-2">

                                                {batteryInfo && (
                                                        <>
                                                                <FontAwesomeIcon icon={batteryInfo.icon} className={batteryInfo.className} />
                                                                <span className={`text-sm font-semibold ${batteryInfo.className}`}>{battery}%</span>
                                                        </>
                                                )}
                                                <span className="font-bold">ID</span>&nbsp; {device.id}
                                        </div>
                                </div>

                        </div>
                </div>
        );
};

export default DeviceCard;
